import { useMutation } from '@tanstack/react-query';
import { Endpoint } from '@/Config/api/endpoint';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { ConversationStatusEnum } from '@/Domain/enum/ConversationStatus.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useChangeConversationStatus } from '@/Domain/hooks/Conversation/useChangeConversationStatus.hook';

export const useConversationReply = (conversation: ConversationDto) => {
  const { handleChangeStatus, canChangeStatus } = useChangeConversationStatus(conversation);
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation<unknown, unknown, Pick<Message, 'content'>>({
    mutationKey: [Endpoint.conversation.reply, conversation.id],
    mutationFn: (data) => ConversationApi.reply(data, conversation.id),
    onSuccess: () => {
      capture(PostHogEventEnum.HumanTakeoverInitiated, {
        conversation_id: conversation.id,
        source: conversation.source,
      });

      if (conversation.resolution_status === ConversationStatusEnum.Resolved && canChangeStatus) {
        handleChangeStatus(ConversationStatusEnum.Open);
      }
    },
  });

  const handleReply = (data: Pick<Message, 'content'>) => mutation.mutate(data);

  return { handleReply, isPending: mutation.isPending };
};
